import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import useHttpClient from './useHttpClient';
import { IProject } from './models/project';

interface IRecentProjectsResponse {
  count: number;
  items: IProject[];
  hasMore: boolean;
  limit: number;
}

/**
 * TanStack Query version of useQueryRecentProjects
 * Fetches recent projects using HTTP client with parameter-based reactivity
 */
const useQueryRecentProjects = (userId: string | null, orgId: number | null) => {
  const httpClient = useHttpClient();

  const queryOptions: UseQueryOptions<IRecentProjectsResponse> = {
    queryKey: ['recentProjects', userId, orgId],
    queryFn: async (): Promise<IRecentProjectsResponse> => {
      if (!userId) {
        throw new Error('User ID not provided');
      }
      if (!orgId) {
        throw new Error('Org ID not provided');
      }

      const response = await httpClient.get(`/users/${userId}/recentprojects`);
      const recentProjects = response.data || [];

      // Transform to match the expected format
      return {
        count: recentProjects.length,
        items: recentProjects,
        hasMore: recentProjects.length >= 50, // Fixed at 50 per original logic
        limit: 50, // Fixed limit as per original implementation
      };
    },
    enabled: userId !== null && userId !== '' && String(userId).length > 0 && orgId !== null && orgId > 0,
  };

  return useQuery(queryOptions);
};

export default useQueryRecentProjects;
